import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, FormControlLabel, Checkbox, Box, Stack,
    MenuItem, Select, InputLabel, FormControl, FormHelperText,
    Typography, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
} from '@mui/material';
import { Shift } from '../../interfaces/shift/Shift';
import { BulkAssignmentRequest } from '../../interfaces/shift/ShiftAssignment';
import { ShiftAssignmentService } from '../../services/shift-assignment.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';
import { displayUsername } from '../../utils/userUtils';

interface SimpleUser {
    id: string;
    username: string;
    name?: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
    institutionId: string;
    shifts: Shift[];
    users: SimpleUser[];
    onSaved: () => void;
}

const USER_CAP = 1000;
const today = () => new Date().toISOString().slice(0, 10);

const BulkAssignmentDialog: React.FC<Props> = ({ open, onClose, institutionId, shifts, users, onSaved }) => {
    const toast = useToast();
    const { t } = useTranslation(['shifts', 'common']);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [shiftId, setShiftId] = useState('');
    const [effectiveFrom, setEffectiveFrom] = useState(today());
    const [effectiveTo, setEffectiveTo] = useState('');
    const [openEnded, setOpenEnded] = useState(true);
    const [replaceExisting, setReplaceExisting] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<{ shiftId?: string; effectiveFrom?: string; effectiveTo?: string; users?: string }>({});

    useEffect(() => {
        if (open) {
            setSelectedIds(new Set());
            setShiftId('');
            setEffectiveFrom(today());
            setEffectiveTo('');
            setOpenEnded(true);
            setReplaceExisting(false);
            setErrors({});
        }
    }, [open]);

    const toggleUser = (id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
        setErrors(prev => ({ ...prev, users: undefined }));
    };

    const selectAll = () => {
        const ids = users.slice(0, USER_CAP).map(u => u.id);
        setSelectedIds(new Set(ids));
        setErrors(prev => ({ ...prev, users: undefined }));
    };

    const clearAll = () => {
        setSelectedIds(new Set());
    };

    const validate = (): boolean => {
        const errs: typeof errors = {};
        if (selectedIds.size === 0) errs.users = t('bulkAssign.selectAtLeastOne');
        if (selectedIds.size > USER_CAP) errs.users = t('bulkAssign.maxUsers', { max: USER_CAP });
        if (!shiftId) errs.shiftId = t('assignmentEdit.shiftRequired');
        if (!effectiveFrom) errs.effectiveFrom = t('assignmentEdit.effectiveFromRequired');
        if (!openEnded && effectiveTo && effectiveTo < effectiveFrom) {
            errs.effectiveTo = t('assignmentEdit.effectiveToInvalid');
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const body: BulkAssignmentRequest = {
                userIds: Array.from(selectedIds),
                shiftId,
                effectiveFrom,
                effectiveTo: openEnded ? null : (effectiveTo || null),
                replaceExisting,
            };
            const result = await ShiftAssignmentService.bulkCreate(institutionId, body);
            toast.showSuccess(t('bulkAssign.saved', { count: result.length }));
            onSaved();
            onClose();
        } catch (err) {
            toast.showApiError(err, t('bulkAssign.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    const activeShifts = shifts.filter(s => s.isActive);
    const submitDisabled = saving || selectedIds.size === 0 || selectedIds.size > USER_CAP;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{t('bulkAssign.title')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    {/* User selection */}
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="subtitle2">
                                {t('bulkAssign.usersSelected', { count: selectedIds.size })}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button size="small" onClick={selectAll} data-testid="bulk-select-all">{t('bulkAssign.selectAllVisible')}</Button>
                                <Button size="small" onClick={clearAll} data-testid="bulk-clear">{t('common:actions.clear')}</Button>
                            </Box>
                        </Box>
                        {errors.users && <FormHelperText error>{errors.users}</FormHelperText>}
                        <Box sx={{ maxHeight: 220, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <List dense disablePadding>
                                {users.map(u => (
                                    <ListItem key={u.id} disablePadding>
                                        <ListItemButton
                                            onClick={() => toggleUser(u.id)}
                                            data-testid={`bulk-user-${u.id}`}
                                        >
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <Checkbox
                                                    edge="start"
                                                    checked={selectedIds.has(u.id)}
                                                    tabIndex={-1}
                                                    disableRipple
                                                    size="small"
                                                />
                                            </ListItemIcon>
                                            <ListItemText primary={u.name || displayUsername(u.username)} />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                        {selectedIds.size > USER_CAP && (
                            <FormHelperText error>{t('bulkAssign.maxUsers', { max: USER_CAP })}</FormHelperText>
                        )}
                    </Box>

                    {/* Shift */}
                    <FormControl fullWidth required error={!!errors.shiftId}>
                        <InputLabel id="bulk-shift-label">{t('fields.shift')}</InputLabel>
                        <Select
                            labelId="bulk-shift-label"
                            value={shiftId}
                            label={t('fields.shift')}
                            onChange={e => { setShiftId(e.target.value); setErrors(prev => ({ ...prev, shiftId: undefined })); }}
                            inputProps={{ 'data-testid': 'bulk-shift-select' }}
                        >
                            {activeShifts.map(s => (
                                <MenuItem key={s.id} value={s.id}>{s.name} ({s.code})</MenuItem>
                            ))}
                        </Select>
                        {errors.shiftId && <FormHelperText>{errors.shiftId}</FormHelperText>}
                    </FormControl>

                    <TextField
                        label={t('fields.effectiveFrom')}
                        type="date"
                        required
                        fullWidth
                        value={effectiveFrom}
                        onChange={e => { setEffectiveFrom(e.target.value); setErrors(prev => ({ ...prev, effectiveFrom: undefined })); }}
                        error={!!errors.effectiveFrom}
                        helperText={errors.effectiveFrom}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ 'data-testid': 'bulk-effective-from' }}
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={openEnded}
                                onChange={e => setOpenEnded(e.target.checked)}
                                data-testid="bulk-open-ended"
                            />
                        }
                        label={t('assignmentEdit.openEnded')}
                    />

                    {!openEnded && (
                        <TextField
                            label={t('fields.effectiveTo')}
                            type="date"
                            fullWidth
                            value={effectiveTo}
                            onChange={e => { setEffectiveTo(e.target.value); setErrors(prev => ({ ...prev, effectiveTo: undefined })); }}
                            error={!!errors.effectiveTo}
                            helperText={errors.effectiveTo}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ 'data-testid': 'bulk-effective-to' }}
                        />
                    )}

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={replaceExisting}
                                onChange={e => setReplaceExisting(e.target.checked)}
                                data-testid="bulk-replace-existing"
                            />
                        }
                        label={t('bulkAssign.replaceExisting')}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>{t('common:actions.cancel')}</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={submitDisabled}
                    data-testid="bulk-save-btn"
                >
                    {saving ? t('saving') : t('bulkAssign.assignButton', { count: selectedIds.size })}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkAssignmentDialog;
